import { useEffect, useState, type FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useStore } from 'zustand';

import { Input } from '@/components/ui';
import {
  FormControl,
  FormItem,
} from '@/features/trade/order/components/order-form-components';
import { useMarketStore } from '@/store/use-markets-store';
import { bigIntToDecimalStr, validatedDecimalStr } from '@/utils/value-format';

import { validateLimitPrice } from '../utils/helpers';

interface PriceInputProps {
  symbol: string;
  label?: string;
  isSuccess?: boolean;
  onPriceChange: (price: string) => void;
}

export const PriceInput: FC<PriceInputProps> = ({
  symbol,
  label = 'Limit Price',
  isSuccess,
  onPriceChange,
}) => {
  const { formState } = useFormContext();

  const priceDecimals = useStore(
    useMarketStore(symbol),
    (state) => state.marketSpec.priceDecimals,
  );

  const [priceInput, setPriceInput] = useState(() =>
    validateLimitPrice(
      symbol,
      bigIntToDecimalStr(
        useMarketStore(symbol).getState().marketData.midPrice,
        priceDecimals,
      ),
    ),
  );

  const handlePriceChange = (value: string) => {
    const validated = validatedDecimalStr(value, Number(priceDecimals), 10);
    const limitPrice = validateLimitPrice(symbol, validated);
    setPriceInput(limitPrice);
    onPriceChange(limitPrice);
  };

  useEffect(() => {
    if (formState.isSubmitting || !isSuccess) return;
    setPriceInput('');
  }, [formState.isSubmitting, isSuccess]);

  return (
    <FormItem className="h-10">
      <p className="text-vestgrey-100">{label}</p>
      <div className="flex flex-1 justify-end">
        <FormControl>
          <Input
            id="price-input"
            className="max-w-[14ch] rounded-none bg-vestgrey-800 font-mono text-base text-foreground transition-colors hover:bg-border"
            value={priceInput}
            onChange={(e) => handlePriceChange(e.target.value)}
            autoComplete="off"
          />
        </FormControl>
        <button
          type="button"
          onClick={() => {
            handlePriceChange(
              bigIntToDecimalStr(
                useMarketStore(symbol).getState().marketData.midPrice,
                priceDecimals,
              ),
            );
          }}
          className="h-10 bg-vestgrey-800 px-3 font-mono text-sm font-medium uppercase text-primary transition-colors hover:bg-border"
        >
          Mid
        </button>
      </div>
    </FormItem>
  );
};
